<script lang="ts" module>
  import type {
    Dates,
    FacilityInformation,
    ProductTypes,
    PurchaseFlowType,
    ResidenceTypes,
  } from "../helpers/types";

  // Structural interface — every flow (purchase, moving, purchaseModule) already
  // satisfies this via createFlowDataCore. Optional members are absent on some
  // flows (canSkipEstimation: moving; additionalProducts/addons+offer: module)
  // and are only consulted when present.
  export interface ChooseAgreementFlow {
    // reads
    readonly estimation: number;
    readonly facilityInformation: FacilityInformation;
    readonly campaign: string;
    readonly purchaseFlowType: PurchaseFlowType;
    readonly selectedProductId: number | null | undefined;
    readonly additionalProducts?: number[];
    readonly requiresLimePurchase?: boolean;
    canSkipEstimation?(): boolean;

    // writes
    setEstimation(v: number): void;
    setZipCode(v: string): void;
    setResidenceType(v: ResidenceTypes): void;
    setProductId(v: number | null): void;
    setProductTypeId(v: number | null): void;
    setProductType(v: ProductTypes | undefined): void;
    setStartDates(v: Dates[] | undefined): void;

    // writes — optional (purchaseModule omits these)
    setAdditionalProducts?(v: number[]): void;
    setExtraFieldTitle?(v: string): void;
  }
</script>

<script lang="ts">
  import EstimationStep from "./EstimationStep.svelte";
  import ProductStep from "./ProductStep.svelte";
  import { getDefaultProductOrder } from "../helpers/helperMethods";
  import { GTMPageView } from "../helpers/tracking";

  interface Props {
    flow: ChooseAgreementFlow;
    /** Flow-level forward — fired once a product is selected & confirmed. */
    onComplete: () => void;
    headerLevel?: 1 | 2;

    // estimation view customization
    estimationHeading?: string;
    estimationText?: string;
    residenceText?: string;
    showZipCode?: boolean;
    zipCodeText?: string;

    // price view customization
    priceHeading?: string;
    productOrder?: string[];
    /** purchaseModule: auto-pick when exactly one product is available. */
    autoSelectSingle?: boolean;

    /** GTM page-view events fired on internal view transitions. Defaults keep the
     * purchase/moving/module names; micro passes its micro-page-* names. */
    productSelectionEvent?: string;
    estimationEvent?: string;

    /** Force the initial view to "estimation" even when the flow could skip it
     * (purchase flow's forceShowEstimation URL-param behavior). */
    forceEstimationView?: boolean;
  }

  let {
    flow,
    onComplete,
    headerLevel = 1,
    estimationHeading = "Hur stor är din elförbrukning?",
    estimationText = "För att få ett ungefärligt pris behöver du uppskatta din årliga elförbrukning.",
    residenceText = "Jag bor i ",
    zipCodeText = "Fyll i ditt postnummer och klicka på ”Beräkna pris” för att se din beräknade månadskostnad.",
    showZipCode = true,
    priceHeading = "Vilket elavtal passar dig?",
    productOrder = getDefaultProductOrder(),
    autoSelectSingle = false,
    productSelectionEvent = "page-product-selection",
    estimationEvent = "page-estimation",
    forceEstimationView = false,
  }: Props = $props();

  // Internal sub-flow state. Flow-level back/next (FlowLayout chrome) never
  // sees this — estimation↔price is component-internal by design.
  // Hydrated at CONSTRUCTION, not onMount: when the initial view is "price"
  // (canSkipEstimation), ProductStep captures the zipcode from flow during its
  // first render — the child re-hydrates its own facilityInformation copy from
  // flow at construction, which happens as soon as this view flips to "price".
  let view = $state<"estimation" | "price">(
    !forceEstimationView && flow.canSkipEstimation?.() ? "price" : "estimation"
  );
</script>

<div>
  {#if view === "estimation"}
    <EstimationStep
      {flow}
      {headerLevel}
      {estimationHeading}
      {estimationText}
      {residenceText}
      {showZipCode}
      {zipCodeText}
      onComplete={() => {
        view = "price";
        // Same event the old separate screens fired via nav.goToPrice().
        GTMPageView(productSelectionEvent);
      }}
    />
  {:else}
    <ProductStep
      {flow}
      {headerLevel}
      {priceHeading}
      {productOrder}
      {autoSelectSingle}
      {onComplete}
      goBack={() => {
        view = "estimation";
        // Same event the old separate screens fired via nav.goToEstimation().
        GTMPageView(estimationEvent);
      }}
    />
  {/if}
</div>
